import { useCallback, useMemo, useState } from 'react';

import { describe } from '../../dates';
import { useLanguage } from '../../contexts/language';
import { useOpenAssignment } from '../../hooks/open-assignment';

import { Button } from '../button';

import styles from './style.module.css';

// One assigned questionnaire: its name and status, who assigned it and when it
// is due, the clinician's instructions, and the way in: Start or Continue.
export function AssignmentCard({ assignment }) {
  const { text, language, translate } = useLanguage();
  const openAssignment = useOpenAssignment();
  const [ busy, setBusy ] = useState(false);
  const [ error, setError ] = useState();
  const { id, assignedBy, dueDate, instructions, repeatEveryDays, questionnaire, draft } = assignment;
  const total = questionnaire.items.length;

  const onOpen = useCallback(function() {
    setBusy(true);

    return openAssignment(assignment).catch(function(failed) {
      setBusy(false);
      setError(failed.message);
    });
  }, [ assignment, openAssignment ]);

  const meta = useMemo(function() {
    const details = [];

    if (assignedBy != null) {
      details.push(text.fromClinician.replace('{name}', assignedBy));
    }

    if (dueDate != null) {
      details.push(text.dueOn.replace('{date}', describe({ day: dueDate, language, short: true })));
    }

    if (details.length === 0) {
      return null;
    }

    return <span className={ styles.meta }>{ details.join(' · ') }</span>;
  }, [ assignedBy, dueDate, text, language ]);

  const note = useMemo(function() {
    if (instructions == null) {
      return null;
    }

    return <p className={ styles.instructions }>“{ instructions }”</p>;
  }, [ instructions ]);

  // Not started, or a draft under way: the chip, the progress line and the
  // way in change together.
  const { status, progress, action } = useMemo(function() {
    if (draft != null) {
      return {
        status: <span className={ `${ styles.chip } ${ styles.draft }` }>{ text.inProgress }</span>,
        progress: text.answeredOf.replace('{answered}', draft.answers.length).replace('{total}', total),
        action: <Button disabled={ busy } onClick={ onOpen }>{ text.continue }</Button>
      };
    }

    let repeats = null;

    if (repeatEveryDays != null) {
      repeats = text.repeatsEvery.replace('{days}', repeatEveryDays);
    }

    return {
      status: <span className={ `${ styles.chip } ${ styles.new }` }>{ text.notStarted }</span>,
      progress: repeats,
      action: <Button disabled={ busy } onClick={ onOpen }>{ text.start }</Button>
    };
  }, [ draft, repeatEveryDays, total, busy, onOpen, text ]);

  const message = useMemo(function() {
    if (error == null) {
      return null;
    }

    return <span className={ styles.error } role='alert'>{ translate(error) }</span>;
  }, [ error, translate ]);

  return (
    <article className={ styles.card } aria-labelledby={ `assignment-${ id }` }>
      <div className={ styles.header }>
        <h3 id={ `assignment-${ id }` } className={ styles.name }>{ questionnaire.name }</h3>

        { status }
      </div>

      { meta }

      { note }

      <div className={ styles.footer }>
        <span className={ styles.progress }>{ progress }</span>

        { action }
      </div>

      { message }
    </article>
  );
}
